import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { Trophy, ChevronRight, Sparkles, Bookmark, MessageCircle } from "lucide-react";
import { levelColor } from "../lib/gamificationApi";

const BOARDS = [
    { id: "helpful_guides",    label: "Helpful Local Guides", icon: Sparkles,       blurb: "XP from trail notes, reviews, and safety reports." },
    { id: "trail_contributors",label: "Trail Contributors",   icon: MessageCircle,  blurb: "Trail notes + safety reports." },
    { id: "most_saved",        label: "Most Saved Adventures",icon: Bookmark,       blurb: "Top adventures by save count." },
];
const PERIODS = [
    { id: "week",  label: "This week" },
    { id: "month", label: "This month" },
    { id: "all",   label: "All time" },
];

export default function Leaderboards() {
    const [board, setBoard] = useState("helpful_guides");
    const [period, setPeriod] = useState("week");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        api.get(`/leaderboards?board=${board}&period=${period}&limit=20`)
            .then(({ data }) => { if (!cancel) setData(data); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [board, period]);

    const isAdvBoard = board === "most_saved";

    return (
        <div data-testid="leaderboards-page">
            <TopBar title="Leaderboards" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
                <header>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-gray">Reward what matters</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tight">Leaderboards</h1>
                    <p className="text-sm text-slate-gray mt-1 max-w-xl">Helpfulness, contribution, and exploration — never speed or risk.</p>
                </header>

                <div data-testid="board-tabs" className="flex flex-wrap gap-2">
                    {BOARDS.map((b) => {
                        const Icon = b.icon;
                        const active = b.id === board;
                        return (
                            <button
                                key={b.id}
                                onClick={() => setBoard(b.id)}
                                data-testid={`board-tab-${b.id}`}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                                    active ? "bg-midnight-pine text-white" : "bg-snowcap border border-ridge-line text-granite hover:border-midnight-pine"
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {b.label}
                            </button>
                        );
                    })}
                </div>

                <div data-testid="period-tabs" className="flex flex-wrap gap-1 bg-snowcap border border-ridge-line rounded-full p-1 w-fit">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setPeriod(p.id)}
                            data-testid={`period-tab-${p.id}`}
                            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                                period === p.id ? "bg-ember-orange text-white" : "text-granite hover:bg-trail-tan/40"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                <div className="bg-snowcap border border-ridge-line rounded-xl p-2">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-slate-gray">
                            <Trophy className="w-8 h-8 mx-auto mb-2 animate-pulse" /> Crunching numbers…
                        </div>
                    ) : !data?.rows?.length ? (
                        <div className="p-8 text-center text-sm text-slate-gray">
                            <Trophy className="w-8 h-8 mx-auto mb-2 text-trail-tan" />
                            No data yet for this board + window. Check back soon.
                        </div>
                    ) : isAdvBoard ? (
                        <AdventureRows rows={data.rows} scoreLabel={data.score_label} />
                    ) : (
                        <UserRows rows={data.rows} scoreLabel={data.score_label} />
                    )}
                </div>
            </div>
        </div>
    );
}

function UserRows({ rows, scoreLabel }) {
    return (
        <ol data-testid="leaderboard-user-rows" className="divide-y divide-ridge-line">
            {rows.map((r, i) => {
                const u = r.user || {};
                const lvl = u.level_slug;
                return (
                    <li key={u.user_id || i} data-testid={`lb-row-${u.user_id}`} className="flex items-center gap-3 px-4 py-3">
                        <span className={`font-display font-black text-2xl w-8 text-center ${
                            i === 0 ? "text-ember-orange" : i === 1 ? "text-slate-gray" : i === 2 ? "text-trail-tan" : "text-midnight-pine"
                        }`}>{i + 1}</span>
                        <div className="w-10 h-10 rounded-full bg-trail-tan overflow-hidden flex items-center justify-center text-sm font-bold text-midnight-pine shrink-0">
                            {u.avatar
                                ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                                : (u.name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <Link to={`/profile/${u.user_id}`} className="font-bold text-sm text-midnight-pine truncate hover:text-ember-orange block">
                                {u.name}
                            </Link>
                            <div className="flex items-center gap-2 text-xs text-slate-gray">
                                {u.username && <span>@{u.username}</span>}
                                {lvl && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: levelColor(lvl) }}>
                                        {lvl.replace(/_/g, " ")}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-display font-black text-xl text-midnight-pine">{r.score?.toLocaleString?.() || r.score}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-gray">{scoreLabel}</div>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

function AdventureRows({ rows, scoreLabel }) {
    return (
        <ol data-testid="leaderboard-adv-rows" className="divide-y divide-ridge-line">
            {rows.map((r, i) => {
                const a = r.adventure || {};
                const u = r.user || {};
                const cover = a.cover_photo || (a.photos && a.photos[0]);
                return (
                    <li key={r.adventure_id || i} data-testid={`lb-adv-${r.adventure_id}`} className="px-4 py-3">
                        <Link to={`/adventures/${r.adventure_id}`} className="flex items-center gap-3 group">
                            <span className={`font-display font-black text-2xl w-8 text-center ${
                                i === 0 ? "text-ember-orange" : i === 1 ? "text-slate-gray" : i === 2 ? "text-trail-tan" : "text-midnight-pine"
                            }`}>{i + 1}</span>
                            <div className="w-14 h-14 rounded-md overflow-hidden bg-trail-tan shrink-0">
                                {cover && <img src={cover} alt={a.title} className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm text-midnight-pine truncate group-hover:text-ember-orange">{a.title || r.adventure_id}</div>
                                <div className="text-xs text-slate-gray truncate">
                                    {u.name && <>by <span className="font-semibold">{u.name}</span> · </>}
                                    {a.activity && <span className="capitalize">{a.activity}</span>}
                                    {a.general_area && <> · {a.general_area}</>}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-display font-black text-xl text-midnight-pine">{r.score}</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-gray">{scoreLabel}</div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-gray group-hover:text-ember-orange" />
                        </Link>
                    </li>
                );
            })}
        </ol>
    );
}
